import { Box, Flex, Text, ThemingProps } from '@chakra-ui/react';
import React, { createRef, useCallback, useRef, useState } from 'react';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import SparkelIcon from '../common/icon/SparkelIcon';

interface DragAndDropProps {
  children?: React.ReactNode;
  allowedMimeTypes?: string[];
  allowedExtensions?: string[];
  uploadFunction: (files: FileList) => void;
  onClick?: () => void;
  borderRadius?: ThemingProps['size'];
}

const DragAndDrop = ({
  children,
  allowedMimeTypes,
  allowedExtensions,
  uploadFunction,
  borderRadius,
}: DragAndDropProps) => {
  const dropdownElement = createRef<HTMLDivElement>();
  const [state, setState] = useState({
    isDragging: false,
    isSupportedFile: false,
  });
  let dragCounter = useRef(0);

  const handleDragIn = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current++;

      // Will evaluate to false if any of the files are not supported
      let isSupportedFiles = true;
      if (allowedMimeTypes) {
        isSupportedFiles = Array.from(e.dataTransfer.items).reduce(
          (acc, item) => acc && allowedMimeTypes.includes(item.type),
          true
        );
      }
      if (isSupportedFiles && allowedExtensions) {
        isSupportedFiles = Array.from(e.dataTransfer.files).reduce(
          (acc, file) =>
            acc && allowedExtensions.includes(file.name.split('.').pop() || ''),
          true
        );
      }

      setState({
        isDragging: true,
        isSupportedFile: isSupportedFiles,
      });
    },
    [allowedExtensions, allowedMimeTypes]
  );

  const handleDragOut = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;

    if (dragCounter.current > 0) return;
    setState({
      isDragging: false,
      isSupportedFile: false,
    });
  }, []);

  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!state.isSupportedFile) {
        setState({
          isDragging: false,
          isSupportedFile: false,
        });
      } else if (e.dataTransfer.items && e.dataTransfer.files.length > 0) {
        uploadFunction(e.dataTransfer.files);
        setState({
          isDragging: false,
          isSupportedFile: false,
        });
      }
    },
    [state.isSupportedFile, uploadFunction]
  );

  let dropElement: React.ReactElement | null = null;

  if (state.isDragging && state.isSupportedFile) {
    dropElement = (
      <Flex
        position="absolute"
        top="0"
        left="0"
        height="100%"
        width="100%"
        backgroundColor="white"
        zIndex={0}
        border="1px dashed"
        borderColor="gray.200"
        _dark={{
          borderColor: 'gray.600',
          backgroundColor: 'black',
        }}
        opacity={0.7}
        borderRadius={borderRadius ?? 'xl'}
        alignItems={'center'}
        justifyContent="center"
      >
        <SparkelIcon icon={faUpload} />
        <Text marginLeft={2}>Drop file to upload</Text>
      </Flex>
    );
  } else if (state.isDragging && !state.isSupportedFile) {
    dropElement = (
      <Flex
        position="absolute"
        top="0"
        left="0"
        height="100%"
        width="100%"
        backgroundColor="red.300"
        borderColor="red.200"
        _dark={{
          borderColor: 'red.600',
          backgroundColor: 'red.700',
        }}
        zIndex={2}
        border="1px dashed"
        borderRadius={borderRadius ?? 'xl'}
        alignItems={'center'}
        justifyContent="center"
      >
        <Text>This file type is not supported</Text>
      </Flex>
    );
  }

  return (
    <Box
      ref={dropdownElement}
      padding="2"
      position="relative"
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <Box marginX={2}>{children}</Box>
      {dropElement}
    </Box>
  );
};

export default DragAndDrop;
